// CRM Data Adapter - transforms CRM synced data into mock data types
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { fetchPresalesPipelineNames, excludePresalesDeals } from '@/lib/presalesFilter';
import { 
  SalesRep, Account, Contact, Lead, Opportunity, Activity, Funnel, FunnelStage,
  DealStage, LeadStatus, LeadSource, ICPTier, Industry, CompanySize, Region,
  StageHistoryEntry
} from './types';
import type { ICPSuggestion } from './icpAnalysis';

type CRMDeal = Tables<'crm_synced_deals'>;
type CRMContact = Tables<'crm_synced_contacts'>;
type CRMActivity = Tables<'crm_synced_activities'>;
type CRMOrg = Tables<'crm_synced_organizations'>;
type CRMTask = Tables<'crm_synced_tasks'>;

// Map CRM deal status+stage to internal DealStage
function mapDealStage(deal: CRMDeal): DealStage {
  if (deal.status === 'won') return 'Closed Won';
  if (deal.status === 'lost') return 'Closed Lost';
  
  const stageLower = (deal.stage || '').toLowerCase();
  if (stageLower.includes('discover') || stageLower.includes('prospect')) return 'Discovery';
  if (stageLower.includes('qualif')) return 'Qualification';
  if (stageLower.includes('propos') || stageLower.includes('apresent')) return 'Proposal';
  if (stageLower.includes('negoci') || stageLower.includes('negot') || stageLower.includes('fecham') || stageLower.includes('clos')) return 'Negotiation';
  
  // Default mapping by position - if we have pipeline stages we could be smarter
  return 'Discovery';
}

function mapLeadStatus(stage: string): LeadStatus {
  const s = stage.toLowerCase();
  if (s.includes('new') || s.includes('novo')) return 'New';
  if (s.includes('contact') || s.includes('contat')) return 'Contacted';
  if (s.includes('qualif') || s.includes('mql')) return 'Qualified';
  if (s.includes('convert') || s.includes('sql') || s.includes('won')) return 'Converted';
  return 'New';
}

/** Deterministic ICP tier based on a string hash (consistent across sessions/users) */
function deterministicICP(seed: string): ICPTier {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  }
  const bucket = Math.abs(hash) % 100;
  if (bucket < 20) return 'A';
  if (bucket < 50) return 'B';
  if (bucket < 80) return 'C';
  return 'D';
}

function deterministicScore(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 7) - hash + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 100;
}

// Calculate ICP tier from saved ICP config suggestions
function calculateICPTierFromConfig(
  suggestions: ICPSuggestion[],
  attributes: { industry?: string; size?: string; region?: string }
): { tier: ICPTier; score: number } {
  if (!suggestions || suggestions.length === 0) {
    const seed = `${attributes.industry || ''}-${attributes.size || ''}-${attributes.region || ''}`;
    return { tier: deterministicICP(seed), score: deterministicScore(seed) };
  }

  // For each tier, check how many criteria match the account attributes
  let bestTier: ICPTier = 'D';
  let bestScore = 0;

  for (const suggestion of suggestions) {
    let matchScore = 0;
    let totalCriteria = suggestion.criteria.length || 1;

    for (const criterion of suggestion.criteria) {
      const attrValue = (() => {
        switch (criterion.dimension) {
          case 'industry': return attributes.industry;
          case 'companySize': return attributes.size;
          case 'geography': return attributes.region;
          default: return undefined;
        }
      })();

      if (attrValue && criterion.value.toLowerCase().includes(attrValue.toLowerCase())) {
        matchScore += criterion.score;
      }
    }

    const normalizedScore = totalCriteria > 0 ? matchScore / totalCriteria : 0;
    if (normalizedScore > bestScore) {
      bestScore = normalizedScore;
      bestTier = suggestion.tier as ICPTier;
    }
  }

  // If no matches found, use deterministic fallback
  if (bestScore === 0) {
    const seed = `${attributes.industry || ''}-${attributes.size || ''}-${attributes.region || ''}`;
    return { tier: deterministicICP(seed), score: deterministicScore(seed) };
  }

  return { tier: bestTier, score: Math.round(bestScore) };
}

export interface CRMDataSet {
  salesReps: SalesRep[];
  accounts: Account[];
  contacts: Contact[];
  leads: Lead[];
  opportunities: Opportunity[];
  activities: Activity[];
  funnels: Funnel[];
}

// Column selections per table — eliminates raw_data and unused fields
const TABLE_COLUMNS: Record<string, string> = {
  crm_synced_deals: 'external_id, name, amount, status, stage, stage_display_name, probability, close_date, closed_at, created_at_crm, updated_at_crm, synced_at, owner_name, owner_email, owner_display_name, contact_external_id, pipeline, pipeline_display_name, deal_source, deal_source_display_name, lost_reason, lost_reason_display_name, mrr, one_time_price, campaign, campaign_display_name, custom_fields, rating, organization_id, crm_connection_id',
  crm_synced_contacts: 'external_id, name, email, phone, title, external_organization_id, synced_at',
  crm_synced_activities: 'external_id, activity_type, subject, notes, occurred_at, owner_name, owner_display_name, deal_external_id, contact_external_id, synced_at',
  crm_synced_organizations: 'external_id, name, industry, company_size, region, employees, annual_revenue, website, state, city',
  crm_synced_tasks: 'external_id, name, description, due_date, status, owner_name, owner_display_name, deal_external_id, type, completed_at, updated_at_crm, created_at_crm, synced_at',
  crm_synced_deal_products: 'deal_external_id, product_external_id, product_name, unit_price, total_price, quantity',
};

// Paginated fetch to overcome the default 1000-row limit per query
async function fetchAllFromTable(
  table: string,
  organizationId: string,
): Promise<any[]> {
  const PAGE_SIZE = 1000;
  const allRows: any[] = [];
  let from = 0;
  const columns = TABLE_COLUMNS[table] || '*';

  while (true) {
    const { data, error } = await supabase
      .from(table as any)
      .select(columns)
      .eq('organization_id', organizationId)
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      console.error(`[crmAdapter] Error fetching ${table} (range ${from}-${from + PAGE_SIZE - 1}):`, error.message);
      break;
    }
    if (!data || data.length === 0) break;
    allRows.push(...data);
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  console.log(`[crmAdapter] Fetched ${allRows.length} rows from ${table}`);
  return allRows;
}

export async function fetchAndTransformCRMData(organizationId: string): Promise<CRMDataSet> {
  // Fetch all CRM data in parallel with pagination to handle >1000 rows
  const [allDeals, crmContacts, crmActivities, crmTasks, crmOrgs, dealProductsRaw, funnelRes, teamsRes, assignmentsRes, presalesNames] = await Promise.all([
    fetchAllFromTable('crm_synced_deals', organizationId),
    fetchAllFromTable('crm_synced_contacts', organizationId),
    fetchAllFromTable('crm_synced_activities', organizationId),
    fetchAllFromTable('crm_synced_tasks', organizationId),
    fetchAllFromTable('crm_synced_organizations', organizationId),
    fetchAllFromTable('crm_synced_deal_products' as any, organizationId),
    supabase.from('funnel_stage_config').select('*').eq('organization_id', organizationId).order('stage_order'),
    supabase.from('sales_teams').select('id, name').eq('organization_id', organizationId),
    supabase.from('rep_team_assignments').select('rep_name, sales_team_id, quota, monthly_quotas').eq('organization_id', organizationId),
    fetchPresalesPipelineNames(organizationId),
  ]);

  // Exclude presales deals from all analytics
  const deals = excludePresalesDeals(allDeals, presalesNames);
  console.log(`[crmAdapter] Excluded ${allDeals.length - deals.length} presales deals (${presalesNames.join(', ')})`);

  // Build deal products lookup: deal_external_id -> product names
  const dealProductsMap = new Map<string, { names: string[]; products: any[] }>();
  for (const dp of dealProductsRaw) {
    const key = (dp as any).deal_external_id;
    if (!key) continue;
    if (!dealProductsMap.has(key)) dealProductsMap.set(key, { names: [], products: [] });
    const entry = dealProductsMap.get(key)!;
    entry.names.push((dp as any).product_name || 'Product');
    entry.products.push(dp);
  }
  console.log(`[crmAdapter] Deal products loaded: ${dealProductsRaw.length} products across ${dealProductsMap.size} deals`);


  const funnelConfig = funnelRes.data || [];
  const salesTeams = teamsRes.data || [];
  const teamAssignments = assignmentsRes.data || [];

  // Build team and quota lookup: rep_name -> team name, rep_name -> quota
  const teamById = new Map(salesTeams.map(t => [t.id, t.name]));
  const repTeamMap = new Map<string, string>();
  const repQuotaMap = new Map<string, number>();
  const repMonthlyQuotasMap = new Map<string, Record<string, number>>();
  teamAssignments.forEach(a => {
    if (a.sales_team_id) {
      const teamName = teamById.get(a.sales_team_id);
      if (teamName) repTeamMap.set(a.rep_name, teamName);
    }
    if (a.quota) repQuotaMap.set(a.rep_name, Number(a.quota));
    if (a.monthly_quotas && typeof a.monthly_quotas === 'object') {
      repMonthlyQuotasMap.set(a.rep_name, a.monthly_quotas as Record<string, number>);
    }
  });

  // Build SalesReps from unique owner names — skip unresolved hex IDs
  const isHexId = (v: string) => /^[0-9a-f]{20,32}$/i.test(v);
  const ownerNames = [...new Set(deals.map(d => d.owner_name).filter((n): n is string => !!n && !isHexId(n)))];
  const salesReps: SalesRep[] = ownerNames.map((name, i) => ({
    id: `crm-rep-${i}`,
    name,
    email: deals.find(d => d.owner_name === name)?.owner_email || `${name.toLowerCase().replace(/\s/g, '.')}@company.com`,
    team: repTeamMap.get(name) || '',
    quota: repQuotaMap.get(name) || 500000,
    monthlyQuotas: repMonthlyQuotasMap.get(name) || {},
    hireDate: new Date('2023-01-01'),
    isActive: true,
  }));

  const repByName = new Map(salesReps.map(r => [r.name, r]));

  // Fetch saved ICP config
  const { data: orgSettings } = await supabase
    .from('organization_settings')
    .select('icp_config')
    .eq('organization_id', organizationId)
    .maybeSingle();

  const savedICPSuggestions: ICPSuggestion[] = 
    orgSettings?.icp_config && typeof orgSettings.icp_config === 'object' && 
    (orgSettings.icp_config as any).useAISuggestion && (orgSettings.icp_config as any).analysis?.suggestions
      ? (orgSettings.icp_config as any).analysis.suggestions
      : [];

  const hasICPConfig = savedICPSuggestions.length > 0;

  // Build Accounts from CRM organizations
  const accounts: Account[] = crmOrgs.map((org, i) => {
    const orgName = org.name || `Account ${i + 1}`;
    const orgIndustry = org.industry || undefined;
    const orgSize = org.company_size || undefined;
    const orgRegion = org.region || undefined;

    const icpResult = hasICPConfig 
      ? calculateICPTierFromConfig(savedICPSuggestions, { industry: orgIndustry, size: orgSize, region: orgRegion })
      : { tier: deterministicICP(orgName), score: deterministicScore(orgName) };

    return {
      id: `crm-acc-${org.external_id}`,
      name: orgName,
      industry: (orgIndustry || 'Technology') as Industry,
      size: (orgSize || 'Mid-Market') as CompanySize,
      region: (orgRegion || 'LATAM') as Region,
      employees: org.employees || 100,
      annualRevenue: org.annual_revenue ? Number(org.annual_revenue) : 1000000,
      website: org.website || '',
      icpScore: icpResult.score,
      icpTier: icpResult.tier,
    };
  });

  // Build account lookup by CRM external_id for proper deal→account mapping
  const accountByExternalOrgId = new Map(crmOrgs.map((org, i) => [org.external_id, accounts[i]]));

  // If no orgs, create accounts from deal names
  if (accounts.length === 0) {
    const dealNames = [...new Set(deals.map(d => d.name).filter(Boolean))];
    dealNames.forEach((name, i) => {
      const seedName = name || `Account ${i}`;
      const icpResult = hasICPConfig 
        ? calculateICPTierFromConfig(savedICPSuggestions, {})
        : { tier: deterministicICP(seedName), score: deterministicScore(seedName) };

      accounts.push({
        id: `crm-acc-${i}`,
        name: seedName,
        industry: 'Technology' as Industry,
        size: 'Mid-Market' as CompanySize,
        region: 'LATAM' as Region,
        employees: 100,
        annualRevenue: 1000000,
        website: '',
        icpScore: icpResult.score,
        icpTier: icpResult.tier,
      });
    });
  }

  // Build Contacts with proper org lookup
  const contacts: Contact[] = crmContacts.map((c, i) => {
    // Look up account by contact's external_organization_id
    const linkedAccount = c.external_organization_id 
      ? accountByExternalOrgId.get(c.external_organization_id) 
      : undefined;
    const fallbackAccount = accounts[i % Math.max(accounts.length, 1)];

    return {
      id: `crm-contact-${c.external_id}`,
      accountId: linkedAccount?.id || fallbackAccount?.id || '',
      firstName: (c.name || '').split(' ')[0] || 'Contact',
      lastName: (c.name || '').split(' ').slice(1).join(' ') || `${i}`,
      email: c.email || '',
      phone: c.phone || undefined,
      title: c.title || '',
      isPrimary: true,
    };
  });

  // Build contact lookup by external_id
  const contactByExternalId = new Map(crmContacts.map((c, i) => [c.external_id, contacts[i]]));

  // Pre-build activity and task lookup Maps (O(n) once instead of O(n*m) per deal)
  const activitiesByDealId = new Map<string, typeof crmActivities>();
  for (const a of crmActivities) {
    if (a.deal_external_id) {
      const existing = activitiesByDealId.get(a.deal_external_id);
      if (existing) existing.push(a);
      else activitiesByDealId.set(a.deal_external_id, [a]);
    }
  }
  const tasksByDealId = new Map<string, typeof crmTasks>();
  for (const t of crmTasks) {
    if (t.deal_external_id) {
      const existing = tasksByDealId.get(t.deal_external_id);
      if (existing) existing.push(t);
      else tasksByDealId.set(t.deal_external_id, [t]);
    }
  }

  // Build Opportunities from deals with proper account lookup
  const opportunities: Opportunity[] = deals.map((deal, i) => {
    const rep = repByName.get(deal.owner_name || '') || salesReps[0];
    
    // Look up account by deal's contact → org link (raw_data no longer fetched)
    const contactExtId = deal.contact_external_id;
    const contactOrg = contactExtId ? crmContacts.find(c => c.external_id === contactExtId)?.external_organization_id : null;
    const linkedAccount = contactOrg ? accountByExternalOrgId.get(contactOrg) : undefined;
    const fallbackAccount = accounts[i % Math.max(accounts.length, 1)];
    const account = linkedAccount || fallbackAccount;
    
    // Look up contact by deal's contact_external_id
    const linkedContact = deal.contact_external_id ? contactByExternalId.get(deal.contact_external_id) : undefined;
    const fallbackContact = contacts[i % Math.max(contacts.length, 1)];
    const contact = linkedContact || fallbackContact;
    
    const stage = mapDealStage(deal);
    
    const createdAt = deal.created_at_crm ? new Date(deal.created_at_crm) : new Date(deal.synced_at);
    const closeDate = deal.close_date ? new Date(`${deal.close_date}T00:00:00`) : deal.closed_at ? new Date(deal.closed_at) : undefined;
    const now = new Date();
    const daysOpen = Math.max(1, Math.round((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24)));
    const daysInStage = Math.max(1, Math.round(daysOpen * 0.3));

    const stageHistory: StageHistoryEntry[] = [{ stage, enteredAt: createdAt }];

    // Aggregate notes from pre-built Maps (O(1) lookup instead of O(n) filter)
    const dealActivities = activitiesByDealId.get(deal.external_id) || [];
    const dealTasks = tasksByDealId.get(deal.external_id) || [];
    const activityNotes = dealActivities
      .map(a => [a.notes, a.subject].filter(Boolean).join(' '))
      .join(' ');
    const taskNotes = dealTasks
      .map(t => [t.description, t.name].filter(Boolean).join(' '))
      .join(' ');
    const combinedNotes = [activityNotes, taskNotes].filter(Boolean).join(' ').trim();

      // Resolve product from deal products
      const dealProducts = dealProductsMap.get(deal.external_id);
      const productName = dealProducts?.names?.join(', ') || 'CRM Product';
      const firstProduct = dealProducts?.products?.[0] as any;

      return {
        id: `crm-opp-${deal.external_id}`,
        accountId: account?.id || '',
        contactId: contact?.id || '',
        ownerId: rep?.id || '',
        name: deal.name || `Deal ${i + 1}`,
        stage,
        crmStageName: deal.stage || undefined,
        amount: deal.amount || 0,
        productId: firstProduct ? `crm-product-${firstProduct.product_external_id}` : 'crm-product',
        isAnnual: false,
        probability: deal.probability || (stage === 'Closed Won' ? 100 : stage === 'Closed Lost' ? 0 : 50),
        closeDate,
        createdAt,
        lastActivityAt: new Date(deal.updated_at_crm || deal.synced_at),
        stageHistory,
        daysInStage,
        daysOpen,
        account,
        contact,
        owner: rep,
        product: { id: firstProduct ? `crm-product-${firstProduct.product_external_id}` : 'crm-product', name: productName, tier: 'Growth' as any, monthlyPrice: firstProduct?.unit_price || 0, annualPrice: (firstProduct?.unit_price || 0) * 12, description: '' },
        notes: combinedNotes || undefined,
        rawData: {},
      } as Opportunity;
  });

  // Build Leads from contacts (simplified - each contact = a lead)
  const leads: Lead[] = crmContacts.slice(0, 500).map((c, i) => {
    const rep = salesReps[i % salesReps.length];
    const linkedAccount = c.external_organization_id 
      ? accountByExternalOrgId.get(c.external_organization_id) 
      : undefined;
    const fallbackAccount = accounts[i % Math.max(accounts.length, 1)];
    const account = linkedAccount || fallbackAccount;
    const contact = contacts[i];
    
    return {
      id: `crm-lead-${c.external_id}`,
      accountId: account?.id || '',
      contactId: contact?.id || '',
      ownerId: rep?.id || '',
      source: 'Organic' as LeadSource,
      status: 'Qualified' as LeadStatus,
      score: deterministicScore(c.external_id),
      createdAt: new Date(c.synced_at),
      lastActivityAt: new Date(c.synced_at),
      account,
      contact,
      owner: rep,
    };
  });

  // If no contacts to make leads from, derive leads from deals
  if (leads.length === 0) {
    deals.forEach((deal, i) => {
      const rep = repByName.get(deal.owner_name || '') || salesReps[0];
      const contactExtId2 = deal.contact_external_id;
      const contactOrg2 = contactExtId2 ? crmContacts.find(c => c.external_id === contactExtId2)?.external_organization_id : null;
      const linkedAccount = contactOrg2 ? accountByExternalOrgId.get(contactOrg2) : undefined;
      const fallbackAccount = accounts[i % Math.max(accounts.length, 1)];
      const account = linkedAccount || fallbackAccount;
      const status = deal.status === 'won' ? 'Converted' : deal.status === 'lost' ? 'Unqualified' : 'Qualified';
      
      leads.push({
        id: `crm-lead-${deal.external_id}`,
        accountId: account?.id || '',
        contactId: '',
        ownerId: rep?.id || '',
        source: (['Organic', 'Paid', 'Referral', 'Outbound'] as LeadSource[])[i % 4],
        status: status as LeadStatus,
        score: deterministicScore(deal.external_id),
        createdAt: deal.created_at_crm ? new Date(deal.created_at_crm) : new Date(deal.synced_at),
        lastActivityAt: new Date(deal.synced_at),
        account,
        owner: rep,
      });
    });
  }

  // Build Activities (prefer explicit CRM activities; fallback to completed tasks)
  const taskDerivedActivities: Activity[] = (crmTasks as CRMTask[])
    .filter((t) => t.completed_at || ['done', 'completed'].includes((t.status || '').toLowerCase()))
    .map((t) => {
      const createdAt = t.completed_at || t.updated_at_crm || t.created_at_crm || t.due_date || t.synced_at;
      return {
        id: `crm-task-act-${t.external_id}`,
        type: mapActivityType(t.type || t.name),
        subject: t.name || t.type || 'Task Activity',
        description: t.description || undefined,
        opportunityId: t.deal_external_id ? `crm-opp-${t.deal_external_id}` : undefined,
        ownerId: repByName.get(t.owner_name || '')?.id || salesReps[0]?.id || '',
        createdAt: new Date(createdAt),
      } as Activity;
    });

  const activitiesFromCRM: Activity[] = crmActivities.map((a) => ({
    id: `crm-act-${a.external_id}`,
    type: mapActivityType(a.activity_type),
    subject: a.subject || 'Activity',
    description: a.notes || undefined,
    opportunityId: a.deal_external_id ? `crm-opp-${a.deal_external_id}` : undefined,
    ownerId: repByName.get(a.owner_name || '')?.id || salesReps[0]?.id || '',
    createdAt: a.occurred_at ? new Date(a.occurred_at) : new Date(a.synced_at),
  }));

  const activities: Activity[] = activitiesFromCRM.length > 0 ? activitiesFromCRM : taskDerivedActivities;

  // Build Funnel from config or defaults
  const stages: FunnelStage[] = funnelConfig.length > 0
    ? funnelConfig.map(f => ({
        id: f.id,
        name: f.stage_name,
        order: f.stage_order,
        category: f.category as FunnelStage['category'],
        color: f.color,
      }))
    : [
        { id: 'fs-1', name: 'New Lead', order: 1, category: 'lead' as const, color: 'hsl(var(--chart-1))' },
        { id: 'fs-2', name: 'Contacted', order: 2, category: 'lead' as const, color: 'hsl(var(--chart-2))' },
        { id: 'fs-3', name: 'MQL', order: 3, category: 'lead' as const, color: 'hsl(var(--chart-3))' },
        { id: 'fs-4', name: 'SQL', order: 4, category: 'qualified' as const, color: 'hsl(var(--chart-4))' },
        { id: 'fs-5', name: 'Discovery', order: 5, category: 'pipeline' as const, color: 'hsl(var(--chart-5))' },
        { id: 'fs-6', name: 'Proposal', order: 6, category: 'pipeline' as const, color: 'hsl(var(--chart-1))' },
        { id: 'fs-7', name: 'Negotiation', order: 7, category: 'pipeline' as const, color: 'hsl(var(--chart-2))' },
        { id: 'fs-8', name: 'Closed Won', order: 8, category: 'won' as const, color: 'hsl(var(--success))' },
        { id: 'fs-9', name: 'Closed Lost', order: 9, category: 'lost' as const, color: 'hsl(var(--destructive))' },
      ];

  const funnels: Funnel[] = [{
    id: 'crm-funnel',
    name: 'CRM Funnel',
    stages,
    isDefault: true,
    createdAt: new Date(),
  }];

  return { salesReps, accounts, contacts, leads, opportunities, activities, funnels };
}

function mapActivityType(type: string | null): Activity['type'] {
  if (!type) return 'note';
  const t = type.toLowerCase().trim();
  // RD Station task types: call, email, meeting, visit, whatsapp, lunch, task
  if (t === 'call' || t.includes('call') || t.includes('ligaç')) return 'call';
  if (t === 'email' || t.includes('email') || t.includes('e-mail')) return 'email';
  if (t === 'meeting' || t === 'visit' || t === 'lunch' || t.includes('meet') || t.includes('reuni') || t.includes('visita') || t.includes('almoço')) return 'meeting';
  if (t === 'whatsapp') return 'note'; // WhatsApp mapped to note (no dedicated type)
  if (t === 'task' || t.includes('task') || t.includes('taref')) return 'task';
  return 'note';
}
